import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, GripVertical } from "lucide-react";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { useDragReorder } from "@/hooks/useDragReorder";
import TranslateButton from "@/components/TranslateButton";
import ImageUploader from "@/components/ImageUploader";

// Schema MUST match exactly what /src/pages/Calculator.jsx reads:
// name_en/ar, default_ratio, grind_en/ar, temp, time, steps_en[], steps_ar[], icon, image_url
const EMPTY = {
  name_en: "",
  name_ar: "",
  default_ratio: 16,
  grind_en: "",
  grind_ar: "",
  temp: "",
  time: "",
  steps_en: [],
  steps_ar: [],
  icon: "Coffee",
  image_url: "",
};

const linesToArr = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
const arrToLines = (a) => (Array.isArray(a) ? a.join("\n") : (a || ""));

export default function AdminRatioTab() {
  const { isRTL } = useI18n();
  const qc = useQueryClient();
  const { data: methods = [], isLoading } = useQuery({
    queryKey: ["methods"],
    queryFn: endpoints.listMethods,
  });
  const [editing, setEditing] = useState(null);

  const refetch = () => {
    qc.invalidateQueries({ queryKey: ["methods"] });
  };

  const onSave = async (form) => {
    if (!editing) return;
    try {
      const payload = {
        name_en: form.name_en,
        name_ar: form.name_ar,
        default_ratio: parseFloat(form.default_ratio) || 16,
        grind_en: form.grind_en || "",
        grind_ar: form.grind_ar || "",
        temp: form.temp || "",
        time: form.time || "",
        steps_en: linesToArr(form.steps_en),
        steps_ar: linesToArr(form.steps_ar),
        icon: form.icon || "Coffee",
        image_url: form.image_url || "",
      };
      if (editing.id === "new") {
        await endpoints.adminAddMethod(payload);
        toast.success(isRTL ? "تمت الإضافة" : "Added");
      } else {
        await endpoints.adminUpdateMethod(editing.id, payload);
        toast.success(isRTL ? "تم الحفظ — التحديث ظاهر للجميع" : "Saved — visible to everyone");
      }
      refetch();
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isRTL ? "تعذّر الحفظ" : "Save failed"));
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm(isRTL ? "حذف هذه الطريقة؟" : "Delete this method?")) return;
    try {
      await endpoints.adminDeleteMethod(id);
      toast.success(isRTL ? "تم الحذف" : "Deleted");
      refetch();
    } catch {
      toast.error(isRTL ? "تعذّر الحذف" : "Delete failed");
    }
  };

  const move = async (ids) => {
    try {
      await endpoints.adminReorderMethods(ids);
      refetch();
    } catch {
      toast.error(isRTL ? "تعذّر الترتيب" : "Reorder failed");
    }
  };

  const { getDragProps } = useDragReorder(methods, move);

  if (isLoading) return <div className="text-[#5C544F]">…</div>;

  return (
    <div data-testid="admin-ratio-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-[#2A2421]">
          {isRTL ? "طرق التحضير (الريشيو)" : "Brewing Methods (Ratio)"} · {methods.length}
        </h2>
        <Button
          data-testid="ratio-add-method-btn"
          onClick={() => setEditing({ id: "new", data: EMPTY })}
          className="bg-[#2A2421] text-[#F9F6F0] hover:bg-[#3A3030]"
        >
          <Plus className="w-4 h-4 me-1" /> {isRTL ? "طريقة جديدة" : "New method"}
        </Button>
      </div>

      <p className="text-[10px] text-[#5C544F] mb-3">
        {isRTL ? "اسحب البطاقة من مقبض ⋮⋮ لإعادة الترتيب" : "Drag the ⋮⋮ handle to reorder"}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {methods.map((m) => (
          <div
            key={m.id}
            {...getDragProps(m.id)}
            className="rounded-2xl border border-[#2A2421]/10 bg-white p-4 transition-all data-[dragging=true]:opacity-50 data-[drag-over=true]:border-[#C86240] data-[drag-over=true]:bg-[#C86240]/5"
            data-testid={`ratio-method-${m.id}`}
          >
            <div className="flex gap-3">
              <div className="flex items-center text-[#5C544F] hover:text-[#2A2421] cursor-grab active:cursor-grabbing">
                <GripVertical className="w-4 h-4" />
              </div>
              {m.image_url ? (
                <img src={m.image_url} alt="" className="w-16 h-16 rounded-lg object-cover" />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-[#F9F6F0]" />
              )}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#2A2421]">{isRTL ? m.name_ar : m.name_en}</div>
                <div className="text-xs text-[#5C544F]">
                  1:{m.default_ratio} · {isRTL ? m.grind_ar : m.grind_en}
                </div>
                <div className="text-[10px] text-[#5C544F] mt-1">
                  {m.temp} · {m.time} · {(m.steps_en || []).length} {isRTL ? "خطوات" : "steps"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3">
              <Button size="sm" variant="ghost" onClick={() => setEditing({ id: m.id, data: m })} data-testid={`ratio-edit-${m.id}`}>
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => onDelete(m.id)} data-testid={`ratio-delete-${m.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <MethodDialog
          key={editing.id}
          editing={editing}
          onClose={() => setEditing(null)}
          onSave={onSave}
          isRTL={isRTL}
        />
      )}
    </div>
  );
}

function MethodDialog({ editing, onClose, onSave, isRTL }) {
  const initial = { ...EMPTY, ...((editing && editing.data) || EMPTY) };
  const [form, setForm] = useState({
    ...initial,
    steps_en: arrToLines(initial.steps_en),
    steps_ar: arrToLines(initial.steps_ar),
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ratio-method-dialog">
        <DialogHeader>
          <DialogTitle>{editing?.id === "new" ? (isRTL ? "طريقة جديدة" : "New method") : (isRTL ? "تعديل الطريقة" : "Edit method")}</DialogTitle>
          <DialogDescription className="sr-only">
            {isRTL ? "نموذج تعديل طريقة التحضير" : "Brewing method editor"}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldWithTranslate label={isRTL ? "الاسم (عربي)" : "Name (AR)"} value={form.name_ar} onChange={set("name_ar")} testid="ratio-name-ar" sourceText={form.name_en} target="ar" onTranslated={(v) => setForm((f) => ({ ...f, name_ar: v }))} />
            <FieldWithTranslate label={isRTL ? "الاسم (إنجليزي)" : "Name (EN)"} value={form.name_en} onChange={set("name_en")} testid="ratio-name-en" sourceText={form.name_ar} target="en" onTranslated={(v) => setForm((f) => ({ ...f, name_en: v }))} />
            <Field label={isRTL ? "الريشيو الافتراضي (1:x)" : "Default ratio (1:x)"} value={form.default_ratio} onChange={set("default_ratio")} testid="ratio-default" type="number" />
            <Field label={isRTL ? "أيقونة (Lucide)" : "Icon (Lucide)"} value={form.icon} onChange={set("icon")} testid="ratio-icon" />
            <FieldWithTranslate label={isRTL ? "حجم الطحن (عربي)" : "Grind (AR)"} value={form.grind_ar} onChange={set("grind_ar")} testid="ratio-grind-ar" sourceText={form.grind_en} target="ar" onTranslated={(v) => setForm((f) => ({ ...f, grind_ar: v }))} />
            <FieldWithTranslate label={isRTL ? "حجم الطحن (إنجليزي)" : "Grind (EN)"} value={form.grind_en} onChange={set("grind_en")} testid="ratio-grind-en" sourceText={form.grind_ar} target="en" onTranslated={(v) => setForm((f) => ({ ...f, grind_en: v }))} />
            <Field label={isRTL ? "الحرارة (مثال: 92 – 96°C)" : "Temperature (e.g. 92 – 96°C)"} value={form.temp} onChange={set("temp")} testid="ratio-temp" />
            <Field label={isRTL ? "الوقت (مثال: 2:30 – 3:00)" : "Time (e.g. 2:30 – 3:00)"} value={form.time} onChange={set("time")} testid="ratio-time" />
          </div>

          <ImageUploader
            label={isRTL ? "صورة" : "Image"}
            value={form.image_url}
            onChange={(url) => setForm((f) => ({ ...f, image_url: url }))}
            testid="ratio-image-uploader"
            aspect="video"
          />

          <div>
            <div className="flex items-center justify-between gap-2">
              <label className="text-xs uppercase tracking-widest text-[#5C544F]">{isRTL ? "الخطوات (عربي) — كل خطوة في سطر" : "Steps (AR) — one per line"}</label>
              <TranslateButton sourceText={form.steps_en} target="ar" onTranslated={(v) => setForm((f) => ({ ...f, steps_ar: v }))} testid="ratio-steps-ar-translate" />
            </div>
            <Textarea value={form.steps_ar || ""} onChange={set("steps_ar")} rows={6} dir="rtl" data-testid="ratio-steps-ar" />
          </div>
          <div>
            <div className="flex items-center justify-between gap-2">
              <label className="text-xs uppercase tracking-widest text-[#5C544F]">{isRTL ? "الخطوات (إنجليزي) — كل خطوة في سطر" : "Steps (EN) — one per line"}</label>
              <TranslateButton sourceText={form.steps_ar} target="en" onTranslated={(v) => setForm((f) => ({ ...f, steps_en: v }))} testid="ratio-steps-en-translate" />
            </div>
            <Textarea value={form.steps_en || ""} onChange={set("steps_en")} rows={6} data-testid="ratio-steps-en" />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} data-testid="ratio-cancel-btn">{isRTL ? "إلغاء" : "Cancel"}</Button>
            <Button onClick={() => onSave(form)} className="bg-[#2A2421] text-[#F9F6F0]" data-testid="ratio-save-btn">
              {isRTL ? "حفظ" : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#5C544F]">{label}</label>
      <Input value={value ?? ""} onChange={onChange} type={type} data-testid={testid} />
    </div>
  );
}

function FieldWithTranslate({ label, value, onChange, testid, sourceText, target, onTranslated }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <label className="text-xs uppercase tracking-widest text-[#5C544F]">{label}</label>
        <TranslateButton sourceText={sourceText} target={target} onTranslated={onTranslated} testid={`${testid}-translate`} />
      </div>
      <Input value={value ?? ""} onChange={onChange} data-testid={testid} />
    </div>
  );
}
